import { useState, useEffect } from 'react'
import AdminLogin from '../components/AdminLogin'
import AdminLayout from '../components/AdminLayout'
import PageMeta from '../components/PageMeta'

const API_BASE_URL =
  import.meta.env.VITE_HARBOR_OPS_API_URL || "http://127.0.0.1:8000"

interface Automation {
  id: string
  name: string
  description: string
  type: string
  schedule: string
  enabled: boolean
  config: Record<string, any>
  lastRun: string | null
  nextRun: string | null
  runCount: number
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never'
  try {
    return new Date(dateString).toLocaleString()
  } catch {
    return dateString
  }
}

function parseCronExpression(cron: string): string {
  // Simple cron parser for display
  const parts = cron.split(' ')
  if (parts.length !== 5) return cron
  
  const [minute, hour, day, _month, weekday] = parts
  
  // Common patterns
  if (cron === '0 9 * * 1') return 'Every Monday at 9:00 AM UTC'
  if (cron === '0 8 * * *') return 'Every day at 8:00 AM UTC'
  if (cron === '0 0 * * 0') return 'Every Sunday at midnight UTC'
  if (cron === '0 */6 * * *') return 'Every 6 hours'
  if (cron === '0 0 1 * *') return 'First day of every month at midnight UTC'
  
  // Generic description
  let desc = ''
  if (weekday !== '*') {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    desc = `Every ${days[parseInt(weekday)]}`
  } else if (day !== '*') {
    desc = `Day ${day} of every month`
  } else {
    desc = 'Daily'
  }
  
  if (hour !== '*' && minute !== '*') {
    desc += ` at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')} UTC`
  }
  
  return desc || cron
}

function getAutomationDescription(automation: Automation): string[] {
  const bullets: string[] = []
  
  if (automation.type === 'pack_update') {
    bullets.push('Checks for regulation and standard changes')
    bullets.push('Monitors market trends and industry updates')
    bullets.push('Aggregates user feedback patterns')
    bullets.push('Tracks competitive landscape shifts')
    const packs = automation.config?.packs || []
    if (packs.length > 0) {
      bullets.push(`Runs for packs: ${packs.join(', ')}`)
    }
  } else if (automation.type === 'sales_report') {
    bullets.push('Generates daily sales summary')
    bullets.push('Includes revenue by pack')
    bullets.push('Tracks new purchases and customer data')
    const recipients = automation.config?.emailRecipients || []
    if (recipients.length > 0) {
      bullets.push(`Emails to: ${recipients.join(', ')}`)
    }
  }
  
  return bullets
}

export default function AutomationsPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [automations, setAutomations] = useState<Automation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<Automation>>({})
  const [runningId, setRunningId] = useState<string | null>(null)

  const fetchAutomations = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/automations`)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const data = await response.json()
      setAutomations(data.automations || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch automations')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      fetchAutomations()
    }
  }, [isAuthenticated])

  const handleEdit = (automation: Automation) => {
    setEditingId(automation.id)
    setEditForm({
      name: automation.name,
      description: automation.description,
      schedule: automation.schedule,
      enabled: automation.enabled,
      config: { ...automation.config },
    })
  }

  const handleSave = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/automations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      
      setEditingId(null)
      setEditForm({})
      await fetchAutomations()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update automation')
    }
  }

  const handleRun = async (id: string) => {
    setRunningId(id)
    try {
      const response = await fetch(`${API_BASE_URL}/api/automations/${id}/run`, {
        method: 'POST',
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(errorData.detail || `HTTP ${response.status}`)
      }
      
      const result = await response.json()
      alert(`Automation run completed: ${result.status}`)
      await fetchAutomations()
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : 'Failed to run automation'}`)
    } finally {
      setRunningId(null)
    }
  }

  if (!isAuthenticated) {
    return <AdminLogin onAuthSuccess={() => setIsAuthenticated(true)} />
  }

  return (
    <>
      <PageMeta
        title="Automations - Harbor Agent Admin"
        description="Manage scheduled automations and tasks"
      />
      <AdminLayout>
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Automations</h2>
            <p className="text-gray-600">
              Manage scheduled tasks and automated workflows
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <div className="text-gray-600">Loading automations...</div>
            </div>
          ) : automations.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-600">No automations configured</div>
            </div>
          ) : (
            <div className="space-y-4">
              {automations.map((automation) => (
                <div
                  key={automation.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{automation.name}</h3>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            automation.enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {automation.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{automation.description}</p>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <span className="text-gray-600">Schedule:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {parseCronExpression(automation.schedule)}
                          </span>
                          <code className="ml-2 text-xs text-gray-500 bg-gray-100 px-1 rounded">
                            {automation.schedule}
                          </code>
                        </div>
                        <div>
                          <span className="text-gray-600">Last Run:</span>
                          <span className="ml-2 text-gray-900">
                            {formatDate(automation.lastRun)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Run Count:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {automation.runCount}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Type:</span>
                          <span className="ml-2 text-gray-900 capitalize">
                            {automation.type.replace('_', ' ')}
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">
                          What this automation does:
                        </h4>
                        <ul className="space-y-1">
                          {getAutomationDescription(automation).map((bullet, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start">
                              <span className="text-primary-600 mr-2">•</span>
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {editingId === automation.id ? (
                    <div className="border-t border-gray-200 pt-4 mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          value={editForm.name || ''}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={editForm.description || ''}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Schedule (Cron Expression)
                        </label>
                        <input
                          type="text"
                          value={editForm.schedule || ''}
                          onChange={(e) => setEditForm({ ...editForm, schedule: e.target.value })}
                          placeholder="0 9 * * 1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Format: minute hour day month weekday (e.g., "0 9 * * 1" = Monday 9 AM UTC)
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`enabled-${automation.id}`}
                          checked={editForm.enabled ?? automation.enabled}
                          onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`enabled-${automation.id}`} className="ml-2 text-sm text-gray-700">
                          Enabled
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(automation.id)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null)
                            setEditForm({})
                          }}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 border-t border-gray-200 pt-4 mt-4">
                      <button
                        onClick={() => handleEdit(automation)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleRun(automation.id)}
                        disabled={runningId === automation.id || !automation.enabled}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          runningId === automation.id || !automation.enabled
                            ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {runningId === automation.id ? 'Running...' : 'Run Now'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </AdminLayout>
    </>
  )
}

